import { Link } from 'react-router-dom';
import BlueDotsBackground from '../../BlueDotsBackground';

const automation = [
  {
    iconClass: 'fas fa-folder-magnifying-glass',
    title: 'Master Data Visibility',
    description:
      'Unifying your master data with critical HRSA data to deliver a comprehensive, 360-degree view of your customers and their relationship to 340B discounts.',
  },
  {
    iconClass: 'fas fa-project-diagram',
    title: 'Transaction-Driven Accuracy ',
    description:
      'By integrating directly with your transactional data - Chargebacks, Managed Care and Medicaid Utilization - OpenPharma enables precise, high-volume validations to identify duplicate 340B discounts.',
  },
  {
    iconClass: 'fas fa-shield-check',
    title: 'Validations and Disputes',
    description:
      'With a comprehensive suite of 340B Discount validations across both master data and transactions, OpenPharma ensures users can navigate the dispute process with accuracy and confidence.',
  },
  {
    iconClass: 'fas fa-handshake',
    title: 'Dispute Resolution',
    description:
      'OpenPharma facilitates open, good-faith resolution of disputed Medicaid, Managed Care and MFP claims through a secure portal connecting manufacturers, states and pharmacies.',
  },
];

const benefits = [
  {
    iconClass: 'fas fa-check-circle',
    title: 'Comprehensive and Compliant',
    description:
      'Fully aligned with CMS and HRSA guidance, our validation suite delivers a comprehensive review of transactional claims to identify potential 340B duplicate discounts.',
  },
  {
    iconClass: 'fas fa-folder-open',
    title: 'One Central Hub',
    description:
      'From transactional history to customer data sourced directly from HRSA, OpenPharma brings all your 340B information into one centralized, searchable, and easy-to-manage platform.',
  },
];

const differentiators = [
  {
    iconClass: 'fas fa-exchange-alt',
    title: 'Easy Migration',
    description:
      'No matter where your data lives — spreadsheets, legacy systems, or third-party tools — OpenPharma makes the migration process easy, ensuring a smooth transition with minimal disruption.',
  },
  {
    iconClass: 'fas fa-sync-alt',
    title: 'Adaptibility',
    description:
      'With a rapidly evolving regulatory landscape, OpenPharma is built to seamlessly incorporate new regulatory requirements and changes to the 340B landscape as they arise.',
  },
];

export default function Duplicates340B() {
  return (
    <>
      <BlueDotsBackground />
      <section
        id="hero"
        className="relative text-center text-white overflow-hidden font-sans"
      >
        <div className="relative z-10 px-6 min-h-screen flex flex-col justify-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight leading-tight mb-8">
              Are 340B duplicates across Medicaid and Managed Medicaid costing
              you?
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 font-light mb-12">
              OpenPharma offers a powerful set of CLD validations designed to
              detect 340B duplicates and generate defensible disputes both
              in-quarter and through chase-and-pay workflows. It also accepts
              inputs from specialized third-party validators to ensure maximum
              coverage, accuracy, and compliance.
            </p>
            <div className="max-w-xl w-full mx-auto mb-20"></div>
          </div>
        </div>
      </section>

      <section id="automation" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Automated End to End Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {automation.map((lifecycle, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i
                  className={`${lifecycle.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {lifecycle.title}
                </h3>
                <p className="text-gray-300 text-sm">{lifecycle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="benefits" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i className={`${benefit.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="differentiators"
        className="py-16 px-6 text-white text-center"
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            What Sets Us Apart
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {differentiators.map((differentiator, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i
                  className={`${differentiator.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {differentiator.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {differentiator.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="cta" className="py-16 px-6 text-white text-center">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center gap-6">
          <Link to="/contact">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Contact Us
            </button>
          </Link>
          <Link to="/login">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Explore Our Platform
            </button>
          </Link>
        </div>
      </section>
    </>
  );
}
